import type { AppError } from "@/api/runtime";
import { screenOpenResult, type HrbpApplication, type ScreenOpenResultOutput } from "@/api/tools";
import { useRead } from "@/api/useTool";
import { Banner, KV, Pill, Skeleton, fmtDate, fmtMoney } from "@/ui";
import { STAGE_LABEL } from "@/ui/vocabulary";
import { AssessmentChain } from "@/views/plan/AssessmentChain";

/** P-CAND.hrbp is a review record, not an empty identity projection. The masked candidate reads as its token label, the
 * word Candidate and the token's last four characters (as the requisition's Candidates tab names it), in the heading the
 * document title takes, never as the opaque token (UAT F-63). */
export function HrbpApplicationRecord({ rid, aid, application }: { rid: string; aid: string; application: HrbpApplication }) {
  const c = application.card;
  return <div>
    <div className="title-line"><h1 className="page">{`Candidate ${c.token.slice(-4).toUpperCase()}`}</h1><Pill tone="muted">{c.stage_label}</Pill></div>
    <p className="lede"><a href={`#requisitions/${rid}/candidates`}>← {rid} · candidates</a></p>
    <div className="record-grid record-grid--start">
      <section className="card" aria-labelledby="hrbp-application-title">
        <h2 id="hrbp-application-title" className="sec">Application</h2>
        <KV items={[["Entered the pipeline", fmtDate(c.entered_pipeline_at)], ["In this stage since", `${fmtDate(c.stage_entered_at)} · ${c.waiting_business_days} business days`]]} />
      </section>
      <HrbpScreenReview rid={rid} aid={aid} />
      <section className="card" aria-labelledby="hrbp-history-title">
        <h2 id="hrbp-history-title" className="sec">Stage history</h2>
        <ol className="history">{[...application.stage_history].reverse().map((event, i) => <li key={i}><b>{STAGE_LABEL[String(event.to)] ?? String(event.to)}</b><p><time>{fmtDate(String(event.at), true)}</time></p></li>)}</ol>
      </section>
    </div>
    {/* V2 B4 S11 (CR-022): the assessment chain's status and dates, as exercise.open's status projection gives them */}
    <AssessmentChain rid={rid} aid={aid} />
  </div>;
}

export function HrbpScreenReview({ rid, aid }: { rid: string; aid: string }) {
  const q = useRead(["screen", aid], () => screenOpenResult({ requisition_id: rid, application_id: aid }));
  return <ScreenReviewCard pending={q.isPending} error={q.error} data={q.data} onRetry={() => void q.refetch()} />;
}

/** The card's states. A sourced candidate has no screen yet and the read answers NOT_FOUND: that is the empty state, a
 * plain sentence, never a banner over the code (UAT F-22); any other failure keeps the banner and its retry. */
export function ScreenReviewCard({ pending, error, data, onRetry }: {
  pending: boolean; error: AppError | null; data: ScreenOpenResultOutput | undefined; onRetry: () => void;
}) {
  const none = error?.code === "NOT_FOUND";
  return <section className="card" aria-labelledby="hrbp-screen-title">
    <h2 id="hrbp-screen-title" className="sec">Screen review</h2>
    {pending && <Skeleton rows={2} />}
    {none ? <p className="muted">No screen review yet.</p> : error && <Banner error={error} onRetry={onRetry} />}
    {data?.projection === "hrbp" && <>
      <KV items={[
        ["Work authorisation", String(data.form.work_authorisation || "Not recorded")],
        ...(data.above_range && data.form.comp_expectation != null ? [["Compensation expectation", fmtMoney(Number(data.form.comp_expectation), String(data.form.comp_expectation_currency ?? data.comp_range?.currency ?? "USD"))] as [string, string]] : []),
      ]} />
      {data.above_range && <Pill tone="warn">Above the approved range</Pill>}
    </>}
  </section>;
}
